import React, { useMemo, useState, useRef, useCallback } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { F } from '../../styles/theme';
import { coinColor } from './CoinGlyph';
import { COIN_MAP } from '../../utils/coinList';

const RED = '#E4544A';

// ── helpers ─────────────────────────────────────────────────────────
export function smoothSeries(values, w = 3) {
  if (values.length <= w) return values.slice();
  const out = [];
  const half = Math.floor(w / 2);
  for (let i = 0; i < values.length; i++) {
    let sum = 0, count = 0;
    for (let j = Math.max(0, i - half); j <= Math.min(values.length - 1, i + half); j++) {
      sum += values[j]; count++;
    }
    out.push(sum / count);
  }
  return out;
}

export function smoothPath(pts) {
  if (!pts.length) return '';
  if (pts.length < 2) return `M ${pts[0].x} ${pts[0].y}`;
  let d = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(pts.length - 1, i + 2)];
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${c1x.toFixed(1)} ${c1y.toFixed(1)} ${c2x.toFixed(1)} ${c2y.toFixed(1)} ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
  }
  return d;
}

function seededRandomSeries(seed, n, trend = 0, jitter = 6, start = 50) {
  let s = seed * 9301 + 49297;
  const raw = [];
  let cur = start;
  for (let i = 0; i < n; i++) {
    s = (s * 9301 + 49297) % 233280;
    const r = s / 233280;
    cur += (r - 0.5) * jitter + trend;
    raw.push(cur);
  }
  return raw;
}

// ── SpectrumBars (FeaturedAssetCard background art) ─────────────────
export function SpectrumBars({ seed = 1, w = 240, h = 56, color = RED, opacity = 1 }) {
  const bars = useMemo(() => {
    const n = 38;
    let s = seed * 9301 + 49297;
    return Array.from({ length: n }, (_, i) => {
      s = (s * 9301 + 49297) % 233280;
      const r = s / 233280;
      const wave = Math.sin(i * 0.35) * 0.3 + Math.cos(i * 0.18) * 0.25 + r * 0.45 + 0.5;
      return Math.max(0.12, Math.min(1, wave));
    });
  }, [seed]);
  const barW = (w - bars.length * 2) / bars.length;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: 'block', opacity }}>
      <defs>
        <linearGradient id={`sg${seed}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="1" />
          <stop offset="100%" stopColor={color} stopOpacity="0.15" />
        </linearGradient>
      </defs>
      {bars.map((v, i) => (
        <rect
          key={i}
          x={i * (barW + 2)}
          y={h - v * h * 0.92}
          width={barW}
          height={v * h * 0.92}
          rx={barW / 2.4}
          fill={`url(#sg${seed})`}
        />
      ))}
    </svg>
  );
}

// ── AreaSpark (sparkline w/ gradient + tip dot) ─────────────────────
export function AreaSpark({
  seed = 1,
  w = 220,
  h = 60,
  color = RED,
  points = 36,
  trend = 0,
  values, // optional: actual numeric series; otherwise seeded mock
  showDot = true,
  showFill = true,
  responsive = true,
}) {
  const pts = useMemo(() => {
    const raw = values && values.length ? values.slice() : seededRandomSeries(seed, points, trend);
    return smoothSeries(smoothSeries(raw, 3), 3);
  }, [seed, points, trend, values]);
  const min = Math.min(...pts), max = Math.max(...pts);
  const range = max - min || 1;
  const coords = pts.map((v, i) => ({
    x: (i / (pts.length - 1)) * w,
    y: h - 6 - ((v - min) / range) * (h - 12),
  }));
  const path = smoothPath(coords);
  const area = `${path} L ${w} ${h} L 0 ${h} Z`;
  const lastY = coords[coords.length - 1]?.y ?? h / 2;
  const id = `ag${seed}-${color.replace('#', '')}`;
  return (
    <svg
      width={responsive ? '100%' : w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio={responsive ? 'none' : 'xMidYMid meet'}
      style={{ display: 'block' }}
    >
      {showFill && (
        <defs>
          <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.42" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
      )}
      {showFill && <path d={area} fill={`url(#${id})`} />}
      <path d={path} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      {showDot && (
        <>
          <circle cx={w - 1} cy={lastY} r="3" fill={color} />
          <circle cx={w - 1} cy={lastY} r="7" fill={color} opacity="0.22" />
        </>
      )}
    </svg>
  );
}

// ── DonutRing ───────────────────────────────────────────────────────
export function DonutRing({ pct = 75, size = 96, stroke = 10, color = RED }) {
  const { T } = useTheme();
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c * (1 - Math.max(0, Math.min(1, pct / 100)));
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: 'block' }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={T.line} strokeWidth={stroke} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeDasharray={c}
        strokeDashoffset={off}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
}

// ── BigAreaChart (AssetDetail) ──────────────────────────────────────
export function BigAreaChart({
  w = 402,
  h = 180,
  color = RED,
  seed = 1,
  points = 70,
  values,           // [number] OR [timestamp, price]-Tupel von CoinGecko
  markers,          // [{ ts, type: 'buy'|'sell' }] — optional
  formatPrice,      // (num) => string — optional, für den Scrub-Tooltip
  formatTime,       // (ts)  => string — optional, Datum/Zeit im Tooltip
  fillOpacity = 0.32, // Flächen-Deckkraft am Top des Verlaufs (Calm: dezenter)
  grid = false,     // theme-aware Gridlines + Baseline wie der Dashboard-Hero (AnimatedArea)
}) {
  const { T } = useTheme();
  const wrapRef = useRef(null);
  // Scrubbing: idx = angefahrener Stützpunkt, cw = aktuelle Pixelbreite.
  const [hover, setHover] = useState(null);

  const pts = useMemo(() => {
    let raw;
    if (values && values.length) {
      raw = values.map(v => (Array.isArray(v) ? v[1] : typeof v === 'number' ? v : v?.y ?? 0));
    } else {
      raw = seededRandomSeries(seed, points, 0, 4);
    }
    return smoothSeries(smoothSeries(smoothSeries(raw, 5), 5), 3);
  }, [seed, points, values]);
  const min = Math.min(...pts), max = Math.max(...pts);
  const range = max - min || 1;
  const coords = pts.map((v, i) => ({
    x: (i / (pts.length - 1)) * w,
    y: h - 14 - ((v - min) / range) * (h - 28),
  }));
  const path = smoothPath(coords);
  const area = `${path} L ${w} ${h} L 0 ${h} Z`;
  const lastY = coords[coords.length - 1]?.y ?? h / 2;
  const id = `bg${seed}-${color.replace('#', '')}`;

  // Marker auf Chart-Linie projizieren (motion.dev-Sprache: punched dot, kein Glow).
  // X aus Zeitachse (values müssen [ts, price]-Tupel sein), Y linear interpoliert
  // zwischen den geglätteten Stützstellen, damit der Punkt exakt auf der Linie sitzt.
  const markerCoords = useMemo(() => {
    if (!markers || !markers.length) return [];
    if (!values || !values.length || !Array.isArray(values[0])) return [];
    const tsStart = values[0][0];
    const tsEnd = values[values.length - 1][0];
    const tsRange = tsEnd - tsStart;
    if (!(tsRange > 0)) return [];
    return markers
      .filter(m => m.ts >= tsStart && m.ts <= tsEnd)
      .map(m => {
        const tNorm = (m.ts - tsStart) / tsRange;
        const idx = tNorm * (coords.length - 1);
        const i0 = Math.floor(idx);
        const i1 = Math.min(i0 + 1, coords.length - 1);
        const frac = idx - i0;
        const x = tNorm * w;
        const y = coords[i0].y * (1 - frac) + coords[i1].y * frac;
        return { ...m, x, y };
      });
  }, [markers, values, coords, w]);

  // Zeitachse für den Tooltip (nur falls [ts, price]-Tupel vorliegen).
  const tsArr = (values && values.length && Array.isArray(values[0])) ? values.map(v => v[0]) : null;

  // Scrub: Pointer-Events decken Maus UND Touch ab. Auf Mobile lässt
  // touchAction:'pan-y' vertikales Scrollen zu, horizontale Drags scrubben.
  const handleMove = useCallback((clientX) => {
    const el = wrapRef.current;
    if (!el || pts.length < 2) return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return;
    const frac = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    setHover({ idx: Math.round(frac * (pts.length - 1)), cw: rect.width });
  }, [pts.length]);
  const clearHover = useCallback(() => setHover(null), []);

  const hi = hover && hover.idx >= 0 && hover.idx < coords.length ? hover : null;
  const hX = hi ? (hi.idx / (pts.length - 1)) * hi.cw : 0;
  const hYpx = hi ? coords[hi.idx].y : 0;

  return (
    <div
      ref={wrapRef}
      style={{ position: 'relative', width: '100%', height: h, touchAction: 'pan-y', cursor: 'crosshair' }}
      onPointerDown={e => handleMove(e.clientX)}
      onPointerMove={e => handleMove(e.clientX)}
      onPointerUp={clearHover}
      onPointerLeave={clearHover}
      onPointerCancel={clearHover}
    >
      <svg
        width="100%"
        height={h}
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        style={{ display: 'block' }}
      >
        <defs>
          <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={fillOpacity} />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        {grid
          ? /* Dashboard-Sprache (AnimatedArea): theme-aware Linien, unterste solid (Baseline). */
            [0, 0.25, 0.5, 0.75, 1].map((t, i, arr) => {
              const baseline = i === arr.length - 1;
              return (
                <line key={t} x1="0" y1={h * t} x2={w} y2={h * t}
                  stroke={T.line} strokeWidth="1"
                  strokeDasharray={baseline ? '0' : '2 5'}
                  opacity={baseline ? 0.9 : 0.32} />
              );
            })
          : [0.25, 0.5, 0.75].map(t => (
              <line key={t} x1="0" y1={h * t + 4} x2={w} y2={h * t + 4}
                stroke="rgba(255,255,255,0.04)" strokeWidth="1" strokeDasharray="2 4" />
            ))}
        <path d={area} fill={`url(#${id})`} />
        <path d={path} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />

        {/* Buy/Sell-Marker (Calm): ruhige „punched" Punkte auf der Linie statt
            lauter Dreiecke. Kauf = gefüllter Dot, Verkauf = hohler Ring; beide in
            den sanften Tönen (T.gain/T.loss), per Surface-Ring sauber freigestellt.
            WICHTIG: Marker, die auf denselben Pixel fallen (Swap = Verkauf+Kauf zur
            selben Zeit, oder Verkauf direkt neben einem Swap), würden sich sonst mit
            ihrem opaken Surface-Ring gegenseitig auslöschen → optisch bliebe nur EIN
            Typ. Daher pro X-Cluster EIN Marker, der Kauf/Verkauf/beides kodiert. */}
        {(() => {
          const gainC = T.gain || T.green;
          const lossC = T.loss || T.red;
          const clusters = [];
          const byKey = new Map();
          markerCoords.forEach((m) => {
            const key = Math.round(m.x / 6);
            let c = byKey.get(key);
            if (!c) { c = { x: m.x, y: m.y, buy: false, sell: false, n: 0 }; byKey.set(key, c); clusters.push(c); }
            c.x = (c.x * c.n + m.x) / (c.n + 1);
            c.y = (c.y * c.n + m.y) / (c.n + 1);
            c.n += 1;
            if (m.type === 'buy') c.buy = true; else c.sell = true;
          });
          return clusters.map((c, i) => (
            <g key={i}>
              <circle cx={c.x} cy={c.y} r="4.8" fill={T.surface} />
              {c.buy && c.sell ? (
                <>
                  <circle cx={c.x} cy={c.y} r="4" fill={T.surface} stroke={lossC} strokeWidth="1.6" />
                  <circle cx={c.x} cy={c.y} r="2.3" fill={gainC} />
                </>
              ) : c.buy ? (
                <circle cx={c.x} cy={c.y} r="3" fill={gainC} />
              ) : (
                <circle cx={c.x} cy={c.y} r="2.7" fill={T.surface} stroke={lossC} strokeWidth="1.6" />
              )}
            </g>
          ));
        })()}

        {/* Crosshair-Linie beim Scrubben (X skaliert sauber, kein Verzerren). */}
        {hi && (
          <line x1={coords[hi.idx].x} y1={4} x2={coords[hi.idx].x} y2={h - 4}
            stroke={color} strokeWidth="1" strokeOpacity="0.55" strokeDasharray="3 3" />
        )}

        {/* Endpunkt im Dashboard-Stil (AnimatedArea): gefüllt + Surface-Ring, kein Glow-Halo. */}
        <circle cx={w - 1} cy={lastY} r="4.5" fill={color} stroke={T.surface} strokeWidth="2.5" />
      </svg>

      {/* Overlay (HTML, daher kein preserveAspect-Verzerren): Punkt + Tooltip. */}
      {hi && (
        <>
          <div aria-hidden style={{
            position: 'absolute', left: hX, top: hYpx, width: 11, height: 11,
            marginLeft: -5.5, marginTop: -5.5, borderRadius: '50%', background: color,
            boxShadow: `0 0 0 3px ${T.bg}`, pointerEvents: 'none',
          }} />
          {/* Tooltip an FESTER Position oben-rechts (verdeckt den Punkt nicht),
              randlos. */}
          <div style={{
            position: 'absolute', top: 4, right: 6,
            pointerEvents: 'none', background: T.surface, border: 'none',
            borderRadius: 9, padding: '5px 11px', whiteSpace: 'nowrap',
            boxShadow: T.cardShadow, textAlign: 'right',
          }}>
            <div style={{
              fontFamily: F.num || F.mono, fontVariantNumeric: 'tabular-nums',
              fontSize: 13, fontWeight: 700, color: T.text, letterSpacing: '-0.01em',
            }}>
              {formatPrice ? formatPrice(pts[hi.idx]) : pts[hi.idx].toFixed(2)}
            </div>
            {tsArr && formatTime && (
              <div style={{ fontSize: 10, color: T.sub, marginTop: 1, fontFamily: F.family }}>
                {formatTime(tsArr[hi.idx])}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── AllocationTiles (HeroCard) ──────────────────────────────────────
// Quiet-Stack-Design: schlanke 10-px-Bar ohne Gaps (nur 1-px Surface-Trenner),
// außen abgerundet. Coin-Farben werden dezent ueber T.surface getoent, damit
// die Bar nicht knallt. Darunter eine ruhige Legend-Row in Mono mit
// Coin-Dot + sym + %. Mount-Animation: clip-path-Reveal von links,
// 70 ms Stagger pro Segment.
// Memory: feedback_no_pills_borders_glow erlaubt Coin-Farben (Regel betrifft
// nur rot-getönte Container).
export function AllocationTiles({ holdings = [], marketData = null }) {
  const { T } = useTheme();

  const segments = useMemo(() => {
    const arr = holdings
      .map(h => {
        const meta = COIN_MAP[h.sym];
        const md = meta && marketData ? marketData[meta.id] : null;
        return {
          sym: h.sym,
          value: typeof h.value === 'number' ? h.value : (h.menge || h.amount || 0) * (h.price || 0),
          color: coinColor(h.sym),
          image: md?.image || null,
        };
      })
      .filter(it => it.value > 0)
      .sort((a, b) => b.value - a.value);
    const sum = arr.reduce((s, x) => s + x.value, 0) || 1;

    const TOP = 4;
    const top = arr.slice(0, TOP);
    const rest = arr.slice(TOP);
    const restValue = rest.reduce((s, x) => s + x.value, 0);
    const restPct = (restValue / sum) * 100;

    const segs = top.map((p) => ({
      key: p.sym,
      label: p.sym,
      pct: (p.value / sum) * 100,
      color: p.color,
      image: p.image,
      isRest: false,
    }));
    if (rest.length > 0 && restPct > 0.1) {
      segs.push({
        key: 'rest',
        label: `+${rest.length}`,
        pct: restPct,
        color: T.isDark ? '#5A5A66' : '#8A867D',
        image: null,
        isRest: true,
        restCount: rest.length,
      });
    }
    // Visual-Weight nur fuer die Bar (Legend zeigt echte %).
    const MIN_VIS = 4;
    segs.forEach(s => { s.vis = Math.max(s.pct, MIN_VIS); });
    const visSum = segs.reduce((s, x) => s + x.vis, 0);
    segs.forEach(s => { s.vis = (s.vis / visSum) * 100; });
    return segs;
  }, [holdings, marketData, T.isDark]);

  if (!segments.length) {
    return (
      <div style={{
        height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: T.mute, fontSize: 11, fontFamily: F.mono, letterSpacing: '0.04em',
      }}>
        Keine Positionen
      </div>
    );
  }

  const trackBg = T.isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)';
  const dividerBg = T.isDark ? 'rgba(10,10,12,0.55)' : 'rgba(255,255,255,0.85)';

  return (
    <>
      <style>{`
        @keyframes redAllocReveal {
          from { clip-path: inset(0 100% 0 0); }
          to   { clip-path: inset(0 0 0 0); }
        }
        @keyframes redAllocLegendIn {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Schlanke Allocation-Bar — clean, ohne Overlay-Logos */}
      <div style={{
        position: 'relative',
        height: 10,
        borderRadius: 999,
        background: trackBg,
        overflow: 'hidden',
        display: 'flex',
      }}>
        {segments.map((seg, i) => (
          <div
            key={seg.key}
            title={`${seg.label} · ${seg.pct.toFixed(1)}%`}
            style={{
              flex: `${seg.vis} 1 0`,
              minWidth: 0,
              background: seg.color,
              boxShadow: i < segments.length - 1 ? `inset -1px 0 0 0 ${dividerBg}` : 'none',
              animation: `redAllocReveal 700ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 70}ms both`,
            }}
          />
        ))}
      </div>

      {/* Legend unter der Bar — größere Logos, klare Hierarchie */}
      <div style={{
        marginTop: 16,
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px 22px',
        animation: 'redAllocLegendIn 500ms cubic-bezier(0.22, 1, 0.36, 1) 200ms both',
      }}>
        {segments.map((seg) => (
          <div key={seg.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
            {seg.image && !seg.isRest ? (
              <span style={{
                width: 22, height: 22, borderRadius: '50%',
                background: '#FFFFFF',
                border: `1px solid ${seg.color}33`,
                boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.04)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, overflow: 'hidden',
              }}>
                <img
                  src={seg.image}
                  alt={seg.label}
                  width={18}
                  height={18}
                  style={{ borderRadius: '50%', display: 'block' }}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </span>
            ) : (
              <span style={{
                width: 22, height: 22, borderRadius: '50%',
                background: seg.color,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
                fontFamily: F.mono, fontSize: 9, fontWeight: 700,
                color: '#fff', letterSpacing: '0.02em',
              }}>
                {seg.isRest ? `+${seg.restCount}` : ''}
              </span>
            )}
            <span style={{
              fontFamily: F.display,
              fontSize: 12.5,
              fontWeight: 700,
              letterSpacing: '-0.01em',
              color: T.text,
              textTransform: seg.isRest ? 'none' : 'uppercase',
            }}>
              {seg.isRest ? `+${seg.restCount} weitere` : seg.label}
            </span>
            <span style={{
              fontFamily: F.mono,
              fontSize: 11.5,
              fontWeight: 600,
              color: T.sub,
              letterSpacing: '-0.01em',
            }}>
              {seg.pct >= 10 ? seg.pct.toFixed(1) : seg.pct.toFixed(2)}%
            </span>
          </div>
        ))}
      </div>
    </>
  );
}
